class BlogContent extends HTMLElement {
  static MIN_LOOP_SLIDES = 12;

  connectedCallback() {
    this.rte = this.querySelector('[data-blog-content-rte]');
    if (this.rte && this.rte.tagName !== 'RTE-FORMATTER') {
      this.rte = this.rte.querySelector('rte-formatter') || this.rte;
    }

    if (this.rte) {
      this.#promoteInlineImageGroups();
      this.#wrapConsecutiveImages();
    }

    this.#initMediaSliders();
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-blog-content-media]').forEach((el) => {
      this.#teardownMediaSlider(el);
    });
  }

  #clearKeepAlive(el) {
    if (el._blogContentKeepAlive) {
      clearInterval(el._blogContentKeepAlive);
      el._blogContentKeepAlive = null;
    }
  }

  #keepAutoplayAlive(el, swiper) {
    this.#clearKeepAlive(el);
    if (!swiper?.autoplay) return;

    el._blogContentKeepAlive = setInterval(() => {
      if (!el.blogContentSwiper?.autoplay) return;
      if (!el.blogContentSwiper.autoplay.running) {
        el.blogContentSwiper.autoplay.start();
      }
    }, 1000);
  }

  #teardownMediaSlider(el) {
    this.#clearKeepAlive(el);
    if (el.blogContentSwiper) {
      el.blogContentSwiper.destroy(true, true);
      el.blogContentSwiper = null;
    }
  }

  #ensureLoopSlides(swiperEl) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return 0;

    wrapper.querySelectorAll('.swiper-slide[data-blog-content-clone]').forEach((clone) => clone.remove());

    const originals = [...wrapper.querySelectorAll('.swiper-slide:not([data-blog-content-clone])')];
    if (!originals.length) return 0;

    let index = 0;
    while (wrapper.querySelectorAll('.swiper-slide').length < BlogContent.MIN_LOOP_SLIDES) {
      const clone = originals[index % originals.length].cloneNode(true);
      clone.setAttribute('data-blog-content-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(clone);
      index += 1;
    }

    return wrapper.querySelectorAll('.swiper-slide').length;
  }

  #initMediaSliders() {
    const start = () => {
      if (typeof Swiper === 'undefined') {
        window.setTimeout(start, 50);
        return;
      }

      this.querySelectorAll('[data-blog-content-media]').forEach((el) => {
        this.#teardownMediaSlider(el);

        const originalCount = el.querySelectorAll('.swiper-slide:not([data-blog-content-clone])').length;
        if (originalCount < 2) return;

        const autoscroll = el.getAttribute('data-autoscroll') !== 'false';
        const marqueeSpeed = parseFloat(el.dataset.marqueeSpeed || '8', 10);
        const media = el.closest('.blog-content-product__media');
        const slideCount = autoscroll ? this.#ensureLoopSlides(el) : originalCount;

        if (!autoscroll) {
          const wrapper = el.querySelector('.swiper-wrapper');
          wrapper?.querySelectorAll('.swiper-slide[data-blog-content-clone]').forEach((clone) => clone.remove());
        }

        const options = {
          slidesPerView: 'auto',
          spaceBetween: 4,
          loop: autoscroll && slideCount > 1,
          watchOverflow: false,
          mousewheel: false,
          allowTouchMove: true,
          breakpoints: {
            750: {
              spaceBetween: 8,
            },
          },
        };

        if (autoscroll) {
          options.speed = marqueeSpeed * 1000;
          options.autoplay = {
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            waitForTransition: true,
            stopOnLastSlide: false,
          };
          options.on = {
            autoplayStop(swiper) {
              if (swiper.autoplay) requestAnimationFrame(() => swiper.autoplay.start());
            },
            reachEnd(swiper) {
              if (swiper.autoplay) swiper.autoplay.start();
            },
          };
        } else {
          options.speed = 400;
          options.autoplay = false;
          options.navigation = {
            nextEl: media?.querySelector('.blog-content-product__button-next'),
            prevEl: media?.querySelector('.blog-content-product__button-prev'),
          };
        }

        el.blogContentSwiper = new Swiper(el, options);

        if (autoscroll) this.#keepAutoplayAlive(el, el.blogContentSwiper);
      });
    };

    start();
  }

  #isImageOnly(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.matches('img, figure, picture, svg')) return true;
    if (!element.matches('p, div')) return false;

    const media = element.querySelectorAll('img, figure, picture, svg');
    if (media.length === 0) return false;

    const clone = element.cloneNode(true);
    clone.querySelectorAll('img, figure, picture, svg, br').forEach((node) => node.remove());
    return clone.textContent.trim() === '';
  }

  #promoteInlineImageGroups() {
    this.rte.querySelectorAll('p, div').forEach((block) => {
      if (block.closest('.blog-content__image-grid')) return;

      const images = [...block.querySelectorAll(':scope > img, :scope > figure, :scope > picture, :scope > svg')];
      if (images.length < 3) return;

      const clone = block.cloneNode(true);
      clone.querySelectorAll('img, figure, picture, svg, br').forEach((node) => node.remove());
      if (clone.textContent.trim() !== '') return;

      const grid = document.createElement('div');
      grid.className = 'blog-content__image-grid';
      images.forEach((image) => grid.appendChild(image));
      block.replaceWith(grid);
    });
  }

  #wrapConsecutiveImages() {
    const children = [...this.rte.children];
    let run = [];

    const flush = () => {
      while (run.length >= 3) {
        const group = run.splice(0, 3);
        const grid = document.createElement('div');
        grid.className = 'blog-content__image-grid';
        group[0].parentNode?.insertBefore(grid, group[0]);
        group.forEach((node) => grid.appendChild(node));
      }
      run = [];
    };

    children.forEach((child) => {
      if (child.classList?.contains('blog-content__image-grid')) {
        flush();
        return;
      }

      if (this.#isImageOnly(child)) {
        run.push(child);
        return;
      }

      flush();
    });

    flush();
  }
}

if (!customElements.get('blog-content')) {
  customElements.define('blog-content', BlogContent);
}
